/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import {LogLevel} from '@azure/msal-browser';


/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

export const msalConfig = {
    auth: {
        clientId: process.env.REACT_APP_APP_ID, // This is the ONLY mandatory field that you need to supply.
        authority: process.env.REACT_APP_AUTHORITY, // Replace the placeholder with your tenant subdomain 
        redirectUri: process.env.REACT_APP_REDIRECT_URI, // Points to window.location.origin. You must register this URI on Microsoft Entra admin center/App Registration.
        instance: process.env.REACT_APP_INSTANCE,
        authorityMetadata : process.env.REACT_APP_AUTHORITY_METADATA,
        cloudDiscoveryMetadata : process.env.REACT_APP_CLOUD_DISCOVERY_METADATA,
        postLogoutRedirectUri: process.env.REACT_APP_HOME_URL, // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        // console.error(message);
                        return;
                    case LogLevel.Info:
                        // console.info(message);
                        return;
                    case LogLevel.Verbose:
                        // console.debug(message);
                        return;
                    case LogLevel.Warning:
                        // console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
const claimsRequestValue = {
    "id_token": {
        "amr": {
            "essential": true,
            "values": ["ngcmfa"]
        }
    },
    access_token: {
        amr: {
            essential: true,
            values: ['ngcmfa']
        }
    }
};
const claims = JSON.stringify(claimsRequestValue);
export const loginRequest = {
    scopes: [`api://${process.env.REACT_APP_APP_ID}/Validate`],
    // Add following claims to enforce ngcmfa, which means every 15 minutes, user needs to re-authenticate with MFA in order to perform passkey creation/deletion operations.
    // This also trigger passkey during sign-in if user already has passkey registered, as passkey could be a default authentication method with highest priority.
    // Currently, for CIAM tenant, autofill with passkey as first authentication method is supported. Using passkey as a secondary auth method is not supported.
    extraQueryParameters: {
        claims: claims,
    },
};


/**
 * Application configuration for backend services
 * These values are now directly configured instead of using environment variables
 */
export const appConfig = {
    appId: process.env.REACT_APP_APP_ID,
    tenantId: process.env.REACT_APP_TENANT_ID,
    customDomain: process.env.REACT_APP_CUSTOM_DOMAIN,
    apiBaseUrl: process.env.REACT_APP_HIP_URL
};